import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { documentApi } from 'src/api/document';
import { visitApi } from 'src/api/visit';
import { DocumentFileDto, DocumentFileResponseDto } from 'woofwoof-api';
import { ScopedPage, sliceInMemory } from './scopedPage';
import { ScopeMutationKind } from './useRemindersScope';
import { useVisitScope } from './useVisitScope';

export type DocumentsMutationKind = ScopeMutationKind | 'note';

interface DocumentsScopeOptions {
  page: number;
  rowsPerPage: number;
  onMutationSuccess?: (kind: DocumentsMutationKind) => void;
  onMutationError?: (kind: DocumentsMutationKind, error: unknown) => void;
}

export interface DocumentsScope
  extends ScopedPage<DocumentFileResponseDto> {
  isLoading: boolean;
  isVisitScoped: boolean;
  upload: (files: DocumentFileDto[]) => Promise<unknown>;
  remove: (filename: string) => void;
  createNote: (content: string) => Promise<unknown>;
  updateNote: (filename: string, content: string) => Promise<unknown>;
  isUploading: boolean;
  isDeleting: boolean;
  isSavingNote: boolean;
}

/**
 * Documents and text notes for the current scope — the patient's profile when
 * there is one, otherwise this visit alone. See `useVisitScope`.
 *
 * Text notes work on both branches: `POST /api/visit/:visitId/text-notes`
 * already existed for the visit case and resolves profileId/organizationId off
 * the visit, so a guest note lands with `profileId: null`.
 */
export function useDocumentsScope({
  page,
  rowsPerPage,
  onMutationSuccess,
  onMutationError,
}: DocumentsScopeOptions): DocumentsScope {
  const { profileId, visitId, isVisitScoped } = useVisitScope();
  const queryClient = useQueryClient();

  const profileQuery = useQuery(
    ['patient-documents-list', profileId, page, rowsPerPage],
    () =>
      documentApi.documentControllerGetProfileDocumentsInOrganization({
        profileId,
        page,
        limit: rowsPerPage,
      }),
    {
      retry: 1,
      enabled: !isVisitScoped && Boolean(profileId),
      keepPreviousData: true,
    },
  );

  const visitQuery = useQuery(
    ['visit-documents', visitId],
    () => visitApi.visitControllerListVisitDocuments({ visitId: visitId || '' }),
    { retry: 1, enabled: isVisitScoped && Boolean(visitId) },
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(['patient-documents-list', profileId]);
    if (visitId) {
      queryClient.invalidateQueries(['visit-documents', visitId]);
      queryClient.invalidateQueries(['visit-activity', visitId]);
    }
  }, [queryClient, profileId, visitId]);

  const { mutateAsync: uploadMutate, isLoading: isUploading } = useMutation({
    mutationFn: (files: DocumentFileDto[]): Promise<unknown> =>
      isVisitScoped
        ? visitApi.visitControllerAddDocuments({
            visitId: visitId || '',
            documentFileDto: files,
          })
        : documentApi.documentControllerAddProfileDocumentsInOrganization({
            profileId,
            documentFileDto: files,
            visitId: visitId || undefined,
          }),
    onSuccess: () => {
      invalidate();
      onMutationSuccess?.('create');
    },
    onError: (error) => onMutationError?.('create', error),
  });

  const { mutate: deleteMutate, isLoading: isDeleting } = useMutation({
    mutationFn: (filename: string): Promise<unknown> =>
      isVisitScoped
        ? visitApi.visitControllerDeleteVisitDocument({
            visitId: visitId || '',
            filename,
          })
        : documentApi.documentControllerDeleteProfileDocumentInOrganization({
            profileId,
            filename,
          }),
    onSuccess: () => {
      invalidate();
      onMutationSuccess?.('delete');
    },
    onError: (error) => onMutationError?.('delete', error),
  });

  const { mutateAsync: noteMutate, isLoading: isSavingNote } = useMutation({
    mutationFn: ({
      filename,
      content,
    }: {
      filename?: string;
      content: string;
    }): Promise<unknown> => {
      if (filename) {
        return isVisitScoped
          ? visitApi.visitControllerUpdateVisitTextNote({
              visitId: visitId || '',
              filename,
              updateTextNoteDto: { content },
            })
          : documentApi.documentControllerUpdateProfileTextNoteInOrganization({
              profileId,
              filename,
              updateTextNoteDto: { content },
            });
      }
      return isVisitScoped
        ? visitApi.visitControllerCreateVisitTextNote({
            visitId: visitId || '',
            createTextNoteDto: { content },
          })
        : documentApi.documentControllerCreateProfileTextNoteInOrganization({
            profileId,
            createTextNoteDto: { content },
          });
    },
    onSuccess: () => {
      invalidate();
      onMutationSuccess?.('note');
    },
    onError: (error) => onMutationError?.('note', error),
  });

  const paged: ScopedPage<DocumentFileResponseDto> = isVisitScoped
    ? sliceInMemory(visitQuery.data?.data ?? [], page, rowsPerPage)
    : {
        items: profileQuery.data?.data.data ?? [],
        total: profileQuery.data?.data.total ?? 0,
      };

  return {
    ...paged,
    isLoading: isVisitScoped ? visitQuery.isLoading : profileQuery.isLoading,
    isVisitScoped,
    upload: uploadMutate,
    remove: deleteMutate,
    createNote: useCallback(
      (content: string) => noteMutate({ content }),
      [noteMutate],
    ),
    updateNote: useCallback(
      (filename: string, content: string) => noteMutate({ filename, content }),
      [noteMutate],
    ),
    isUploading,
    isDeleting,
    isSavingNote,
  };
}
